import * as React from 'react';
import * as _ from 'underscore';

import { ReadAloudSource } from '../../../Shared/components/readAloud/readAloudSource';
import { CLIP_SOURCE_TYPE, clipSource } from '../../../Shared/libs/speech/clipSource';
import { CONNECT } from '../../../Shared/utils/constants';
import {
  MultipleChoice,
  SentenceFragments,
  getLatestAttempt,
  hashToCollection,
  renderExplanation,
  Cues
} from '../../../Shared/index';
import { Response } from '../../../Shared/quill-marking-logic/src/main';
import { storeResponseInAttempts } from '../../actions.js';
import {
  getGradedResponsesWithCallback,
  getMultipleChoiceResponseOptionsWithCallback,
  saveResponseToCMS,
} from '../../actions/responses.js';
import C from '../../constants';
import EditCaretPositioning from '../../../Shared/libs/editCaretPositioning';
import checkAnswer from '../../libs/checkConnectSentenceCombiningQuestionAnswer';
import RenderFeedback from '../renderForQuestions/feedback';
import { cancelSpeech } from '../../../Shared/libs/speech/speechManager';
import AnswerForm from '../renderForQuestions/renderFormForAnswer.jsx';
import staticStrings from '../../../Shared/libs/speech/staticStrings.json';

const RenderSentenceFragments = SentenceFragments

interface PlayLessonQuestionProps {
  conceptsFeedback: any;
  dispatch: () => void;
  isAdmin: boolean;
  isLastQuestion: boolean;
  languageLocale: string;
  nextQuestion: () => void;
  onHandleToggleQuestion: (question: any) => void;
  prefill: any;
  previewMode: boolean;
  question: any;
  questionToPreview: any;
  key: any;
  showTranslation: boolean;
  translate: (input: string) => string;
}

interface PlayLessonQuestionState {
  editing: boolean;
  response: string;
  responses?: object[];
  finished: boolean;
  multipleChoice: boolean;
  multipleChoiceCorrect?: boolean;
  multipleChoiceResponseOptions?: object[];
  sessionKey?: string;
}

export default class PlayLessonQuestion extends React.Component<PlayLessonQuestionProps,PlayLessonQuestionState> {
  constructor(props) {
    super(props)

    const { question, } = props
    let response = ''
    const numberOfAttempts = question.attempts ? question.attempts.length : null;
    if (numberOfAttempts) {
      const lastSubmitted = question.attempts[numberOfAttempts - 1]
      response =  lastSubmitted.response.text
    }

    this.state = {
      editing: false,
      response,
      finished: false,
      multipleChoice: false
    }
  }

  componentDidMount() {
    const { question } = this.props;
    getGradedResponsesWithCallback(
      question.key,
      (data) => {
        this.setState({ responses: data, });
      }
    );
    getMultipleChoiceResponseOptionsWithCallback(
      question.key,
      (data) => {
        this.setState({ multipleChoiceResponseOptions: _.shuffle(data), });
      }
    );
  }

  shouldComponentUpdate(nextProps: PlayLessonQuestionProps, nextState: PlayLessonQuestionState) {
    const { question, showTranslation } = this.props
    const { response, finished, multipleChoice, responses} = this.state
    if (question !== nextProps.question) {
      return true;
    } else if (response !== nextState.response) {
      return true;
    } else if (finished !== nextState.finished) {
      return true;
    } else if (multipleChoice !== nextState.multipleChoice) {
      return true;
    } else if (responses !== nextState.responses) {
      return true;
    } else if (showTranslation !== nextProps.showTranslation) {
      return true;
    }
    return false;
  }

  componentDidUpdate(prevProps) {
    const { question } = this.props;
    if(prevProps.question.key !== question.key) {
      getMultipleChoiceResponseOptionsWithCallback(
        question.key,
        (data) => {
          this.setState({ multipleChoiceResponseOptions: _.shuffle(data), });
        }
      );
    }
  }

  getInitialValue = () => {
    const { prefill, question } = this.props
    if (prefill) {
      return question.prefilledText;
    }
  }

  removePrefilledUnderscores = () => {
    const { response, } = this.state
    this.setState({ response: response.replace(/_/g, ''), });
  }

  getQuestion = () => {
    const { question } = this.props;
    return question
  }

  getResponse(rid) {
    return (this.getResponses()[rid]);
  }

  getResponses = () => {
    const { responses, } = this.state
    return responses;
  }

  renderSentenceFragments = () => {
    const { question } = this.props;
    return <RenderSentenceFragments prompt={question.prompt} />;
  }

  listCuesAsString = (cues: string[]) => {
    const newCues = cues.slice(0);
    return `${newCues.splice(0, newCues.length - 1).join(', ')} or ${newCues.pop()}.`;
  }

  renderFeedback = (override?: string) => {
    const { question, previewMode, translate, showTranslation, languageLocale } = this.props;
    const latestAttempt = this.handleGetLatestAttempt()
    let sentence;
    if (override) {
      sentence = override;
    } else if (question && question.modelConceptUID) {
      sentence = 'Revise your work. Use the hint below as an example.';
    } else {
      sentence = 'Keep writing! Revise your sentence by changing the order of the ideas.';
    }
    return (
      <RenderFeedback
        languageLocale={languageLocale}
        latestAttempt={latestAttempt}
        listCuesAsString={this.listCuesAsString}
        override={!!override}
        previewMode={previewMode}
        question={question}
        responses={this.getResponses()}
        sentence={sentence}
        showTranslation={showTranslation}
        translate={translate}
      />
    );
  }

  getErrorsForAttempt = () => {
    const questionAttempt = this.handleGetLatestAttempt();
    return _.pick(questionAttempt, ...C.ERROR_TYPES);
  }

  renderCues = () => {
    const { question } = this.props;
    return (
      <Cues
        displayArrowAndText={true}
        includeReadAloudButton={true}
        question={question}
      />
    );
  }

  updateResponseResource(response) {
    const { dispatch, question } = this.props
    const attempts = question.attempts || [];
    const latestAttempt = attempts[attempts.length - 1];
    const isFirstAttempt = !latestAttempt;
    dispatch(saveResponseToCMS(response.response, isFirstAttempt));
  }

  answeredCorrectly = () => {
    const latestAttempt = this.handleGetLatestAttempt();
    const errorsForAttempt = _.keys(this.getErrorsForAttempt()).length > 0;
    return (latestAttempt && latestAttempt.response && !errorsForAttempt && latestAttempt.response.optimal);
  }

  checkAnswer = (e: React.SyntheticEvent) => {
    const { question, dispatch, } = this.props;
    const { editing, response, } = this.state
    if (editing && this.getResponses() && Object.keys(this.getResponses()).length) {
      this.removePrefilledUnderscores();
      const submittedResponse = checkAnswer(question, response, this.getResponses());
      this.updateResponseResource(submittedResponse)
      dispatch(storeResponseInAttempts(submittedResponse))
      this.setState({ editing: false });
    }
  }

  completeMultiChoice = () => {
    this.setState({ finished: true, });
  }

  toggleDisabled = () => {
    const { editing, } = this.state
    if (editing && this.getResponses() && Object.keys(this.getResponses()).length) {
      return '';
    }
    return 'disabled';
  }

  onChange = (e: string, element: React.ReactElement) => {
    const { response, } = this.state
    if (e !== response) {
      const caretPosition = EditCaretPositioning.saveSelection(element)
      this.setState({ editing: true, response: e, }, () => EditCaretPositioning.restoreSelection(element, caretPosition))
    }
  }

  readyForNext = () => {
    const { question, previewMode } = this.props
    const hasAttemptsOrIsPreview = (question.attempts && question.attempts.length > 0) || previewMode;
    if(!hasAttemptsOrIsPreview) {
      return false;
    }
    const latestAttempt = this.handleGetLatestAttempt();
    if (latestAttempt && latestAttempt.response) {
      const errors = _.keys(this.getErrorsForAttempt());
      return latestAttempt.response.optimal && errors.length === 0;
    }
  }

  getProgressPercent = () => {
    const { question, } = this.props

    return question.attempts.length / 3 * 100;
  }

  getDisabledStatus = () => {
    const { previewMode, isLastQuestion } = this.props;
    return previewMode && isLastQuestion
  }

  finish = () => {
    this.setState({ finished: true, });
  }

  handleNextQuestionClick = () => {
    const { nextQuestion, } = this.props
    cancelSpeech()

    nextQuestion();
    this.setState({ response: '', });
  }

  renderNextQuestionButton = () => {
    const { isLastQuestion, showTranslation, translate, languageLocale } = this.props
    let buttonText = isLastQuestion ? 'Next' : 'Next question'
    buttonText = showTranslation ? translate(`buttons^${buttonText.toLowerCase()}`) : buttonText
    const disabledStatus = this.getDisabledStatus();
    const disabledStyle = disabledStatus ? 'disabled' : '';
    return (<button className={`quill-button focus-on-light green contained large ${disabledStyle}`} disabled={disabledStatus} lang={languageLocale} onClick={this.handleNextQuestionClick} type="button">{buttonText}</button>);
  }

  renderFinishedQuestionButton = () => {
    const { showTranslation, translate, languageLocale } = this.props
    const buttonText = showTranslation ? translate('buttons^next') : 'Next'
    const nextF = () => {
      this.setState({ finished: true, });
    };
    return (<button className="quill-button focus-on-light green contained large" lang={languageLocale} onClick={nextF} type="button">{buttonText}</button>);
  }

  renderMultipleChoiceButton = () => {
    const { showTranslation, translate, languageLocale } = this.props
    const buttonText = showTranslation ? translate('buttons^next') : 'Next'
    const nextF = () => {
      this.setState({ multipleChoice: true, });
    };
    return (<button className="quill-button focus-on-light green contained large" lang={languageLocale} onClick={nextF} type="button">{buttonText}</button>);
  }

  finishQuestion = () => {
    this.setState({ finished: true, });
  }

  getNegativeConceptResultsForResponse(conceptResults) {
    return hashToCollection(conceptResults).filter(cr => !cr.correct);
  }

  renderConceptExplanation = () => {
    const { conceptsFeedback, question, showTranslation, languageLocale } = this.props
    const explanationFor = (key: string) => renderExplanation({ activityType: CONNECT, conceptsFeedback, data: conceptsFeedback.data[key], key, languageLocale, showTranslation, });
    //TODO: update Response interface in quill-marking-logic to fix Boolean/boolean type checking
    const latestAttempt:{response: Response}|undefined = this.handleGetLatestAttempt();

    // we do not want to show concept feedback if a response is optimal
    if (!latestAttempt || !latestAttempt.response || latestAttempt.response.optimal) { return }

    if (latestAttempt.response.author || [true, false].includes(latestAttempt.response.optimal)) {
      // we only want to show response-specific (ie, concept-result-specific) concept feedback if the response is matched
      // The presence of 'author' indicates a match via algorithm, and an explicitly-set 'optimal' property indicates a match via explicit human grading

      if (latestAttempt.response.conceptResults) {
        const negativeConcepts = this.getNegativeConceptResultsForResponse(latestAttempt.response.conceptResults);
        const negativeConceptWithConceptFeedback = negativeConcepts.find(c => {
          return conceptsFeedback.data[c.conceptUID]
        })
        if (negativeConceptWithConceptFeedback) {
          const key = negativeConceptWithConceptFeedback.conceptUID
          return explanationFor(key);
        }
      } else if (latestAttempt.response.concept_results) {
        const negativeConcepts = this.getNegativeConceptResultsForResponse(latestAttempt.response.concept_results);
        const negativeConceptWithConceptFeedback = negativeConcepts.find(c => {
          return conceptsFeedback.data[c.conceptUID]
        })
        if (negativeConceptWithConceptFeedback) {
          const key = negativeConceptWithConceptFeedback.conceptUID
          return explanationFor(key);
        }
      }

    } else {
      // we only want to show question-level concept feedback if the response is unmatched
      if (question && question.modelConceptUID) {
        const key = question.modelConceptUID
        return explanationFor(key);
      }

      if (question.conceptID) {
        const key = question.conceptID
        return explanationFor(key);
      }
    }
  }

  multipleChoiceFinishQuestion = () => {
    this.setState({ multipleChoiceCorrect: true, }, () => {
      this.finishQuestion();
    });
  }

  handleGetLatestAttempt = () => {
    const { question } = this.props;
    const latestAttempt = getLatestAttempt(question.attempts);
    return latestAttempt;
  }

  render() {
    const { question, isAdmin, previewMode, showTranslation, translate, languageLocale } = this.props
    const { response, finished, multipleChoice, multipleChoiceCorrect, multipleChoiceResponseOptions  } = this.state
    const questionID = question.key;

    if (question) {
      const sharedProps = {
        value: response,
        question,
        isAdmin,
        response,
        responses: this.getResponses(),
        getResponse: this.getResponse,
        feedback: this.renderFeedback(),
        initialValue: this.getInitialValue(),
        key: questionID,
        questionID,
        id: 'playQuestion',
        sentenceFragments: this.renderSentenceFragments(),
        cues: this.renderCues(),
        className: 'fubar',
        previewMode,
        showTranslation,
        translate,
        languageLocale
      };
      let component;
      const maxAttemptsSubmitted = question.attempts && question.attempts.length > 4;
      const someAttemptsSubmitted = question.attempts && question.attempts.length > 0;
      if (finished) {
        component = (
          <AnswerForm
            {...sharedProps}
            disabled
            finished
            multipleChoiceCorrect={multipleChoiceCorrect}
            nextQuestionButton={this.renderNextQuestionButton()}
          />
        );
      } else if (multipleChoice) {
        component = (
          <MultipleChoice
            answers={multipleChoiceResponseOptions}
            languageLocale={languageLocale}
            next={this.multipleChoiceFinishQuestion}
            prompt={this.renderSentenceFragments()}
            showTranslation={showTranslation}
            translate={translate}
          />
        );
      } else if (maxAttemptsSubmitted) {
        if (this.answeredCorrectly()) {
          component = (
            <AnswerForm
              {...sharedProps}
              disabled
              nextQuestionButton={this.renderFinishedQuestionButton()}
            />
          );
        } else {
          sharedProps.feedback = this.renderFeedback(staticStrings['connect.multipleChoiceIntro']);
          component = (
            <AnswerForm
              {...sharedProps}
              disabled
              nextQuestionButton={this.renderMultipleChoiceButton()}
            />
          );
        }
      } else if (someAttemptsSubmitted) {
        if (this.readyForNext()) {
          component = (
            <AnswerForm
              {...sharedProps}
              disabled
              nextQuestionButton={this.renderFinishedQuestionButton()}
            />
          );
        } else {
          component = (
            <AnswerForm
              {...sharedProps}
              checkAnswer={this.checkAnswer}
              conceptExplanation={this.renderConceptExplanation}
              handleChange={this.onChange}
              spellCheck={(question.attempts && question.attempts.length > 3)}
              toggleDisabled={this.toggleDisabled()}
            />
          );
        }
      } else {
        component = (
          <AnswerForm
            {...sharedProps}
            checkAnswer={this.checkAnswer}
            handleChange={this.onChange}
            toggleDisabled={this.toggleDisabled()}
          />
        );
      }
      return (
        <ReadAloudSource source={clipSource(CLIP_SOURCE_TYPE.QUESTION, question.key)}>
          <div className="student-container-inner-diagnostic">
            {component}
          </div>
        </ReadAloudSource>
      );
    }
    return (<p>Loading...</p>);
  }
}
